/**
 * useResolvedTerminology — Single source of truth for all UI terminology (DRR-189)
 *
 * Fetches resolved terminology from the backend `resolvedTerminology` query.
 * Each term is backed by a statutory definition (tag:def:*) with statute reference,
 * legal definition text, and acceptable alternatives.
 *
 * This hook replaces:
 * - useFhirLabels() — FHIR resource type → display term
 * - useTenantBranding().terminology — FERP primitive → display term
 * - FhirLabelsContext — context distribution to subgraph packages
 * - ANNOTATION_TYPE_DOMAIN_LABELS — hardcoded frontend domain labels
 *
 * The backend returns both statutory-backed terms (from tag:def:* definitions)
 * and legacy terms (from st-0-0.yaml fhir_labels) during migration.
 *
 * Usage:
 * ```tsx
 * const { getLabel, getDefinition, terms, loading } = useResolvedTerminology();
 * getLabel('Patient')     // → "Resident" (from 144G.08 Subd 7)
 * getLabel('workflow')    // → "Care Plan" (from tenant terminology)
 * getDefinition('Patient') // → { display, statuteRef, legalDefinition, ... }
 * ```
 */

import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useAuth } from '@/auth/AuthContext';

// =============================================================================
// QUERY
// =============================================================================

const RESOLVED_TERMINOLOGY_QUERY = gql`
  query ResolvedTerminology {
    resolvedTerminology {
      concept
      display
      source
      definitionRef
      statuteRef
      legalDefinition
      acceptableAlternatives
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface ResolvedTerm {
  concept: string;
  display: string;
  source: 'STATUTORY' | 'OVERRIDE' | 'ADVISORY' | 'LEGACY';
  definitionRef: string | null;
  statuteRef: string | null;
  legalDefinition: string | null;
  acceptableAlternatives: string[];
}

export interface UseResolvedTerminologyReturn {
  /** Get the display term for a concept. Falls back to the concept name. */
  getLabel: (concept: string) => string;
  /** Get the full resolved term with statute reference and legal definition. */
  getDefinition: (concept: string) => ResolvedTerm | undefined;
  /** All resolved terms indexed by concept. */
  terms: Record<string, ResolvedTerm>;
  /** Whether the query is still loading. */
  loading: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

// Query result type (manual — codegen will generate this after SDL sync)
interface ResolvedTerminologyQueryData {
  resolvedTerminology: ResolvedTerm[];
}

export function useResolvedTerminology(): UseResolvedTerminologyReturn {
  const { isAuthenticated } = useAuth();
  const { data, loading } = useQuery<ResolvedTerminologyQueryData>(RESOLVED_TERMINOLOGY_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated, // Don't query before login
  });

  const terms = useMemo<Record<string, ResolvedTerm>>(() => {
    const list: ResolvedTerm[] = data?.resolvedTerminology ?? [];
    const map: Record<string, ResolvedTerm> = {};
    for (const term of list) {
      map[term.concept] = term;
    }
    return map;
  }, [data?.resolvedTerminology]);

  const getLabel = useMemo(
    () => (concept: string): string => terms[concept]?.display ?? concept,
    [terms],
  );

  const getDefinition = useMemo(
    () => (concept: string): ResolvedTerm | undefined => terms[concept],
    [terms],
  );

  return { getLabel, getDefinition, terms, loading };
}
